import { createPromptSearchParams } from "@/lib/inspector/navigation";
import { useNavigate } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { EmptyState, InspectorSection, PromptItem } from "./InspectorSection";

interface Prompt {
  name: string;
  description?: string;
}

interface PromptsSectionProps {
  prompts: Prompt[];
  projectId: string;
  isDeployed: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

export function PromptsSection({
  prompts,
  projectId,
  isDeployed,
  isExpanded,
  onToggle,
}: PromptsSectionProps) {
  const navigate = useNavigate();

  const handlePromptNavigate = (promptName: string) => {
    const searchParams = createPromptSearchParams(promptName);
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: searchParams,
    });
  };

  const handleMorePromptsNavigate = () => {
    const firstPrompt = prompts[0];
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: {
        tab: "inspector",
        selectedPrompt: firstPrompt?.name || undefined,
      },
    });
  };

  return (
    <InspectorSection
      title="Prompts"
      icon={MessageSquare}
      count={isDeployed ? prompts.length : 0}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {isDeployed && prompts.length > 0 ? (
        <>
          {prompts.slice(0, 3).map((prompt, index) => (
            <PromptItem
              key={`${prompt.name}-${index}`}
              prompt={prompt}
              onNavigate={handlePromptNavigate}
            />
          ))}
          {prompts.length > 3 && (
            <button
              type="button"
              onClick={handleMorePromptsNavigate}
              className="text-xs text-muted-foreground hover:text-primary transition-colors text-center pt-1 w-full"
            >
              +{prompts.length - 3} more prompts available
            </button>
          )}
        </>
      ) : (
        <EmptyState
          icon={MessageSquare}
          message={
            !isDeployed ? "Deploy to discover prompts" : "No prompts available"
          }
        />
      )}
    </InspectorSection>
  );
}
